import * as Snackbar from '@synergy/shared/snackbar/data-access';
import { DefaultMapping } from '@synergy/models';
import { Dispatch } from 'redux';
import { PaginatedResponse } from '@synergy/api';
import { PoolNotification } from '@synergy/shared/snackbar/data-access';

import { Api } from '../../api';
import { AppState } from '../../store/models/app-state';
import { Attribute } from '../models/attribute';
import { ErrorMessage } from '../models/enums/error-message';

export const CANCEL_CREATE_ATTRIBUTE = 'CANCEL_CREATE_ATTRIBUTE';
export const CANCEL_DELETE_ATTRIBUTE = 'CANCEL_DELETE_ATTRIBUTE';
export const CANCEL_PATCH_ATTRIBUTE = 'CANCEL_PATCH_ATTRIBUTE';
export const CANCEL_UPDATE_MAPPING = 'CANCEL_UPDATE_MAPPING';
export const CREATE_ATTRIBUTE_COMPLETED = 'CREATE_ATTRIBUTE_COMPLETED';
export const DELETE_ATTRIBUTE_COMPLETED = 'DELETE_ATTRIBUTE_COMPLETED';
export const PATCH_ATTRIBUTE_COMPLETED = 'PATCH_ATTRIBUTE_COMPLETED';
export const RECEIVE_ATTRIBUTE_DEFAULT_MAPPING = 'RECEIVE_ATTRIBUTE_DEFAULT_MAPPING';
export const RECEIVE_ATTRIBUTE_DEFAULT_MAPPINGS = 'RECEIVE_ATTRIBUTE_DEFAULT_MAPPINGS';
export const RECEIVE_ATTRIBUTES = 'RECEIVE_ATTRIBUTES';
export const REQUEST_ATTRIBUTE_MAPPINGS = 'REQUEST_ATTRIBUTE_MAPPINGS';
export const REQUEST_ATTRIBUTES = 'REQUEST_ATTRIBUTES';
export const START_CREATE_ATTRIBUTE = 'START_CREATE_ATTRIBUTE';
export const START_DELETE_ATTRIBUTE = 'START_DELETE_ATTRIBUTE';
export const START_PATCH_ATTRIBUTE = 'START_PATCH_ATTRIBUTE';
export const START_UPDATE_MAPPING = 'START_UPDATE_MAPPING';

const requestAttributes = () => {
    return {
        type: REQUEST_ATTRIBUTES,
    };
};

const requestAttributeMappings = () => {
    return {
        type: REQUEST_ATTRIBUTE_MAPPINGS,
    };
};

const receiveAttributes = (attributesPaginatedData: PaginatedResponse<Attribute>) => {
    return {
        type: RECEIVE_ATTRIBUTES,
        attributesPaginatedData,
    };
};

const receiveAttributeMappings = (defaultMappings: PaginatedResponse<DefaultMapping<Attribute>>) => {
    return {
        type: RECEIVE_ATTRIBUTE_DEFAULT_MAPPINGS,
        defaultMappings,
    };
};

const receiveAttributeMapping = (defaultMapping: DefaultMapping<Attribute>) => {
    return {
        type: RECEIVE_ATTRIBUTE_DEFAULT_MAPPING,
        defaultMapping,
    };
};

const startPatch = () => {
    return { type: START_PATCH_ATTRIBUTE };
};

const cancelPatch = () => {
    return { type: CANCEL_PATCH_ATTRIBUTE };
};

const startUpdateMapping = () => {
    return { type: START_UPDATE_MAPPING };
};

const cancelUpdateMapping = () => {
    return { type: CANCEL_UPDATE_MAPPING };
};

const patchCompleted = (attribute: Attribute) => {
    return {
        type: PATCH_ATTRIBUTE_COMPLETED,
        attribute,
    };
};

const startCreate = () => {
    return { type: START_CREATE_ATTRIBUTE };
};

const cancelCreate = () => {
    return { type: CANCEL_CREATE_ATTRIBUTE };
};

const createCompleted = (attribute: Attribute) => {
    return {
        type: CREATE_ATTRIBUTE_COMPLETED,
        attribute,
    };
};

const deleteCompleted = (attributeId: number) => {
    return {
        type: DELETE_ATTRIBUTE_COMPLETED,
        attributeId,
    };
};

const startDelete = () => {
    return {
        type: START_DELETE_ATTRIBUTE,
    };
};

const cancelDelete = () => {
    return {
        type: CANCEL_DELETE_ATTRIBUTE,
    };
};

export const fetchAttributes = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestAttributes());

        const response = await api.attributes.cget(searchParams);

        if (response.success) {
            dispatch(receiveAttributes(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const fetchAttributesMapping = (searchParams: URLSearchParams) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(requestAttributeMappings());

        const response = await api.attributes.getDefaultMapping(searchParams);

        if (response.success) {
            dispatch(receiveAttributeMappings(response.result));
        } else {
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const deleteAttribute = (attributeId: number) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startDelete());
        const response = await api.attributes.deleteAttribute(attributeId);

        if (response.success === true) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.attributeDeleted')));
            dispatch(deleteCompleted(attributeId));
        } else {
            dispatch(cancelDelete());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const patchAttribute = (attribute: Partial<Attribute>) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startPatch());
        const response = await api.attributes.updateAttributes(attribute);

        if (response.success === true) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.attributeUpdated')));
            dispatch(patchCompleted(response.result));
        } else {
            dispatch(cancelPatch());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const createAttribute = (attribute: Partial<Attribute>) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startCreate());
        const response = await api.attributes.sendNewAttribute(attribute);

        if (response.success === true) {
            dispatch(Snackbar.enqueue(PoolNotification.success('app.snackbar.attributeCreated')));
            dispatch(createCompleted(response.result));
        } else if (response.error?.title === ErrorMessage.NonUniqueProperties) {
            dispatch(cancelCreate());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.configuration.error.nonUniqueKey')));
        } else {
            dispatch(cancelCreate());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};

export const updateDefaultMapping = (mapping: DefaultMapping) => {
    return async (dispatch: Dispatch, _getState: () => AppState, { api }: { api: Api }) => {
        dispatch(startUpdateMapping());
        const response = await api.attributes.updateDefaultMapping(mapping);

        if (response.success === true) {
            dispatch(receiveAttributeMapping(response.result));
        } else {
            dispatch(cancelUpdateMapping());
            dispatch(Snackbar.enqueue(PoolNotification.error('app.snackbar.error')));
        }
    };
};
